<script>
import axios from '@/utils/axios';

export default {
  inject: ['setLoading'],
  data() {
    return {
      loading: true,
      announcements: [],
      search: '',
      typeFilter: '',
      typeOptions: ['general', 'urgent', 'event', 'policy'],
      typeLabels: {
        general: 'General',
        urgent: 'Urgent',
        event: 'Event',
        policy: 'Policy',
      },
      pagination: {
        current_page: 1,
        per_page: 20,
        last_page: 1,
        total: 0,
        links: [],
      },
      editItem: null,
      deleteItem: null,
      statsItem: null,
    };
  },
  mounted() {
    this.fetchAnnouncements();
  },
  methods: {
    async fetchAnnouncements(page = 1) {
      this.loading = true;
      try {
        const response = await axios.get('/hrms/announcements', {
          params: {
            page,
            search: this.search || undefined,
            type: this.typeFilter || undefined,
          },
        });
        this.announcements = response.data.data.data;
        this.pagination = {
          current_page: response.data.data.current_page,
          per_page: response.data.data.per_page,
          last_page: response.data.data.last_page,
          total: response.data.data.total,
          links: response.data.data.links,
        };
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.loading = false;
      }
    },

    onSearch() {
      this.fetchAnnouncements(1);
    },

    clearFilters() {
      this.search = '';
      this.typeFilter = '';
      this.fetchAnnouncements(1);
    },

    typeLabel(t) {
      return this.typeLabels[t] || t || '-';
    },

    typeClass(type) {
      const map = {
        general: 'ann-tag ann-general',
        urgent: 'ann-tag ann-urgent',
        event: 'ann-tag ann-event',
        policy: 'ann-tag ann-policy',
      };
      return map[type] || 'ann-tag ann-general';
    },

    isPublished(row) {
      if (!row.published_at) return false;
      return new Date(row.published_at) <= new Date();
    },

    isExpired(row) {
      if (!row.expires_at) return false;
      return new Date(row.expires_at) < new Date();
    },

    goToPage(page) {
      if (page < 1 || page > this.pagination.last_page) return;
      this.fetchAnnouncements(page);
    },

    // ----- create / edit -----
    goAdd() {
      this.editItem = null;
      this.$refs.announcementModal.create();
      this.$openModal('hrm_announcement_modal');
    },
    goEdit(row) {
      this.editItem = row;
      this.$refs.announcementModal.edit(row);
      this.$openModal('hrm_announcement_modal');
    },
    onSaved() {
      this.fetchAnnouncements(this.pagination.current_page);
    },

    // ----- publish -----
    async publish(row) {
      this.setLoading(true);
      try {
        const res = await axios.post(`/hrms/announcements/${row.id}/publish`);
        this.$swalToast(res.data.message);
        this.fetchAnnouncements(this.pagination.current_page);
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.setLoading(false);
      }
    },

    // ----- read stats -----
    openStats(row) {
      this.statsItem = row;
      this.$refs.statsModal.open(row);
      this.$openModal('hrm_announcement_stats_modal');
    },

    // ----- delete -----
    confirmDelete(row) {
      this.deleteItem = row;
      this.$openModal('hrm_announcement_delete_modal');
    },
    async deleteAnnouncement() {
      this.setLoading(true);
      try {
        const res = await axios.delete(`/hrms/announcements/${this.deleteItem.id}`);
        this.$swalToast(res.data.message);
        this.$closeModal('hrm_announcement_delete_modal');
        this.fetchAnnouncements(this.pagination.current_page);
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.setLoading(false);
      }
    },
  },
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">

      <!-- Header -->
      <div class="d-md-flex d-flex align-items-center justify-content-between page-breadcrumb mb-3">
        <div>
          <h4 class="page-title mb-0">Announcements</h4>
        </div>
        <div class="d-flex align-items-center flex-wrap gap-2">
          <a href="javascript:void(0);" class="btn btn-primary d-flex align-items-center" @click="goAdd">
            <i class="ti ti-circle-plus me-2"></i>Add Announcement
          </a>
        </div>
      </div>

      <!-- Toolbar -->
      <div class="card border-0 shadow-sm mb-3" style="border-radius:14px;">
        <div class="card-body d-flex align-items-center justify-content-between flex-wrap gap-3 py-3 px-4">
          <h5 class="mb-0 fw-bold" style="font-size:14px; color:#0f172a;">
            Announcement List
            <span style="font-size:11px; color:#94a3b8; font-weight:500; margin-left:6px;">({{ pagination.total }})</span>
          </h5>

          <div class="d-flex align-items-center gap-2 flex-wrap">
            <select class="form-select form-select-sm" style="width:auto; min-width:130px;" v-model="typeFilter" @change="onSearch">
              <option value="">All Types</option>
              <option v-for="t in typeOptions" :key="t" :value="t">{{ typeLabel(t) }}</option>
            </select>

            <div class="l-search-wrap">
              <input type="text" class="l-search-input" v-model="search" placeholder="Search announcements..." @keyup.enter="onSearch" />
              <span style="padding:0 10px; color:#94a3b8;"><i class="ti ti-search"></i></span>
            </div>

            <button
              v-if="search || typeFilter"
              type="button"
              class="btn btn-sm btn-light d-flex align-items-center"
              style="height:34px;"
              @click="clearFilters"
              title="Clear filters"
            >
              <i class="ti ti-x me-1"></i>Clear
            </button>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="ann-state">
        <div class="spinner-border text-primary mb-2"></div>
        <div>Loading announcements...</div>
      </div>

      <!-- Empty -->
      <div v-else-if="!announcements.length" class="ann-state">
        <i class="ti ti-speakerphone d-block mb-2" style="font-size:38px; opacity:.35;"></i>
        No announcements found.
      </div>

      <!-- Card Grid -->
      <div v-else class="row g-3">
        <div v-for="item in announcements" :key="item.id" class="col-xxl-4 col-lg-6 col-md-6">
          <div class="ann-card">

            <!-- top: type + actions -->
            <div class="ann-card-top">
              <div class="d-flex align-items-center gap-2">
                <span :class="typeClass(item.type)">{{ typeLabel(item.type) }}</span>
                <span v-if="isPublished(item)" class="ann-st ann-published">Published</span>
                <span v-else class="ann-st ann-draft">Draft</span>
                <span v-if="isExpired(item)" class="ann-st ann-expired">Expired</span>
              </div>
              <div class="dropdown">
                <button class="btn btn-icon btn-sm rounded-circle" type="button" data-bs-toggle="dropdown">
                  <i class="ti ti-dots-vertical"></i>
                </button>
                <ul class="dropdown-menu dropdown-menu-end p-3">
                  <li>
                    <a class="dropdown-item rounded-1" href="javascript:void(0);" @click="goEdit(item)">
                      <i class="ti ti-edit me-1"></i>Edit
                    </a>
                  </li>
                  <li v-if="!isPublished(item)">
                    <a class="dropdown-item rounded-1" href="javascript:void(0);" @click="publish(item)">
                      <i class="ti ti-send me-1"></i>Publish
                    </a>
                  </li>
                  <li>
                    <a class="dropdown-item rounded-1" href="javascript:void(0);" @click="openStats(item)">
                      <i class="ti ti-chart-bar me-1"></i>Read Stats
                    </a>
                  </li>
                  <li><hr class="dropdown-divider" /></li>
                  <li>
                    <a class="dropdown-item rounded-1 text-danger" href="javascript:void(0);" @click="confirmDelete(item)">
                      <i class="ti ti-trash me-1"></i>Delete
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            <!-- title + content -->
            <div class="ann-card-body" @click="goEdit(item)">
              <h6 class="ann-title">{{ item.title }}</h6>
              <p class="ann-content">{{ item.content }}</p>
            </div>

            <!-- meta -->
            <div class="ann-meta">
              <div class="ann-meta-row">
                <i class="ti ti-building"></i>
                <span>{{ item.branch?.name || 'All Branches' }}</span>
              </div>
              <div class="ann-meta-row">
                <i class="ti ti-user"></i>
                <span>{{ item.created_by?.name || '-' }}</span>
              </div>
            </div>

            <!-- footer -->
            <div class="ann-card-foot">
              <span class="ann-date">
                <i class="ti ti-calendar-event me-1"></i>
                {{ item.published_at ? $formatDate(item.published_at) : 'Not scheduled' }}
              </span>
              <span class="ann-date" v-if="item.expires_at">
                <i class="ti ti-calendar-x me-1"></i>
                {{ $formatDate(item.expires_at) }}
              </span>
            </div>

          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="!loading && announcements.length && pagination.last_page > 1" class="d-flex justify-content-center align-items-center gap-2 mt-4">
        <button class="btn btn-sm btn-light" :disabled="pagination.current_page === 1" @click="goToPage(pagination.current_page - 1)">
          <i class="ti ti-chevron-left"></i>
        </button>
        <span style="font-size:12.5px; color:#64748b;">
          Page {{ pagination.current_page }} of {{ pagination.last_page }}
        </span>
        <button class="btn btn-sm btn-light" :disabled="pagination.current_page === pagination.last_page" @click="goToPage(pagination.current_page + 1)">
          <i class="ti ti-chevron-right"></i>
        </button>
      </div>

    </div>
    <app-footer></app-footer>
  </div>

  <!-- Create / Edit Modal -->
  <hrm-announcement-modal ref="announcementModal" @updated="onSaved"></hrm-announcement-modal>

  <!-- Read Stats Modal -->
  <hrm-announcement-stats-modal ref="statsModal"></hrm-announcement-stats-modal>

  <!-- Delete Modal -->
  <div class="modal fade" id="hrm_announcement_delete_modal">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-body text-center">
          <span class="avatar avatar-xl bg-transparent-danger text-danger mb-3">
            <i class="ti ti-trash-x fs-36"></i>
          </span>
          <h4 class="mb-1">Confirm Delete</h4>
          <p class="mb-3">Delete <strong>{{ deleteItem?.title }}</strong>? This can't be undone.</p>
          <div class="d-flex justify-content-center">
            <a href="javascript:void(0);" class="btn btn-light me-3" data-bs-dismiss="modal">Cancel</a>
            <a href="javascript:void(0);" class="btn btn-danger" @click="deleteAnnouncement">Yes, Delete</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.l-search-wrap {
  display: flex; align-items: center;
  border: 1.5px solid #e2e8f0; border-radius: 8px;
  overflow: hidden; background: #fff;
}
.l-search-wrap:focus-within { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,.1); }
.l-search-input {
  border: none; outline: none; height: 34px; padding: 0 10px;
  font-size: 12.5px; color: #1e293b; width: 200px; background: transparent;
}

.ann-state {
  text-align: center; padding: 60px 20px;
  color: #94a3b8; font-size: 13.5px;
  background: #fff; border-radius: 14px;
}

.ann-card {
  background: #fff;
  border: 1px solid #eef2f7;
  border-radius: 14px;
  padding: 16px;
  height: 100%;
  transition: all .18s ease;
  box-shadow: 0 1px 2px rgba(15,23,42,.04);
}
.ann-card:hover {
  border-color: #c7d2fe;
  box-shadow: 0 8px 20px rgba(99,102,241,.1);
}

.ann-card-top {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 12px; flex-wrap: wrap; gap: 6px;
}

.ann-card-body { cursor: pointer; margin-bottom: 12px; }
.ann-title {
  font-size: 14.5px; font-weight: 700; color: #1e293b;
  margin-bottom: 4px;
}
.ann-content {
  font-size: 12.5px; color: #64748b; margin: 0;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
  overflow: hidden;
}

.ann-meta {
  display: flex; flex-direction: column; gap: 6px;
  padding: 10px 0; border-top: 1px solid #f1f5f9;
}
.ann-meta-row {
  display: flex; align-items: center; gap: 8px;
  font-size: 12px; color: #475569; min-width: 0;
}
.ann-meta-row i { font-size: 14px; color: #94a3b8; flex-shrink: 0; }

.ann-card-foot {
  display: flex; align-items: center; justify-content: space-between;
  padding-top: 10px; border-top: 1px solid #f1f5f9; flex-wrap: wrap; gap: 6px;
}
.ann-date { font-size: 11px; color: #94a3b8; }

.ann-tag {
  display: inline-block; font-size: 10.5px; font-weight: 600;
  padding: 3px 10px; border-radius: 20px;
}
.ann-general { background: #eef2ff; color: #4f46e5; }
.ann-urgent { background: #fee2e2; color: #dc2626; }
.ann-event { background: #fef3c7; color: #b45309; }
.ann-policy { background: #e0f2fe; color: #0369a1; }

.ann-st {
  display: inline-block; font-size: 10px; font-weight: 600;
  padding: 3px 10px; border-radius: 20px;
}
.ann-published { background: #dcfce7; color: #16a34a; }
.ann-draft { background: #f1f5f9; color: #94a3b8; }
.ann-expired { background: #fee2e2; color: #dc2626; }
</style>